import { useNavigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { usePlanVersions } from '@/hooks/usePlanDetail'
import { useCurrentVersionStore } from '@/stores/useCurrentVersionStore'
import { formatDate } from '@/utils/formatters'

interface VersionPickerModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  planId: string
  planName: string
}

export function VersionPickerModal({
  open,
  onOpenChange,
  planId,
  planName,
}: VersionPickerModalProps) {
  const navigate = useNavigate()
  const { data: versions, isLoading, isError } = usePlanVersions(planId)
  const setCurrentVersion = useCurrentVersionStore(s => s.setCurrentVersion)

  const handleVersionClick = (versionId: string) => {
    setCurrentVersion(planId, versionId)
    onOpenChange(false)
    navigate(`/plans/${planId}`)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Select Version - {planName}</DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {isError && (
          <p className="text-sm text-destructive">
            Failed to load versions. Please try again.
          </p>
        )}

        {!isLoading && !isError && versions && versions.length > 0 && (
          <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
            {versions.map(version => (
              <div
                key={version.id}
                onClick={() => handleVersionClick(version.id)}
                className="flex items-center justify-between rounded-lg border p-3 cursor-pointer hover:bg-muted/50 transition-colors"
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">
                      {version.alias}
                    </span>
                    {version.is_active && (
                      <Badge className="bg-primary hover:bg-primary text-white text-xs">
                        Active
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {formatDate(version.created_at)}
                  </p>
                  {version.notes && (
                    <p className="text-xs text-muted-foreground italic">
                      {version.notes}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
